// OnboardingContext.tsx - Global state provider for onboarding tours
import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { onboardingService } from '../services/onboardingService';

export interface TourStepConfig {
  id: string;
  targetSelector: string;
  title: string;
  description: string;
  placement: 'top' | 'bottom' | 'left' | 'right' | 'auto';
  spotlightPadding?: number;
  illustration?: ReactNode;
  topAdjustment?: number; // Fine-tune vertical tooltip position (positive = down, negative = up)
  leftAdjustment?: number; // Fine-tune horizontal tooltip position (positive = right, negative = left)
  hideArrow?: boolean; // Hide the arrow pointer
  width?: number; // Custom tooltip width (default: 320)
  onBeforeStep?: () => void; // Callback to run before showing this step
}

export interface TourConfig {
  id: string;
  pageId: string;
  steps: TourStepConfig[];
}

interface OnboardingContextType {
  // State
  isTourActive: boolean;
  currentTourId: string | null;
  currentStepIndex: number;
  completedTours: Record<string, boolean>;
  totalSteps: number;

  // Actions
  startTour: (tourId: string, steps: TourStepConfig[]) => void;
  endTour: () => void;
  nextStep: () => void;
  prevStep: () => void;
  skipTour: () => void;
  goToStep: (index: number) => void;
  markTourComplete: (tourId: string) => void;
  isTourCompleted: (tourId: string) => boolean;
  resetTour: (tourId: string) => void;

  // Current step info
  currentStep: TourStepConfig | null;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

interface OnboardingProviderProps {
  children: ReactNode;
}

export function OnboardingProvider({ children }: OnboardingProviderProps) {
  const [isTourActive, setIsTourActive] = useState(false);
  const [currentTourId, setCurrentTourId] = useState<string | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [currentSteps, setCurrentSteps] = useState<TourStepConfig[]>([]);
  const [completedTours, setCompletedTours] = useState<Record<string, boolean>>({});

  // Load completed tours from localStorage on mount
  useEffect(() => {
    const saved = onboardingService.getCompletedTours();
    setCompletedTours(saved);
  }, []);

  const startTour = useCallback((tourId: string, steps: TourStepConfig[]) => {
    setCurrentTourId(tourId);
    setCurrentSteps(steps);
    setCurrentStepIndex(0);
    setIsTourActive(true);
  }, []);

  const endTour = useCallback(() => {
    if (currentTourId) {
      // Mark as complete when tour finishes naturally
      onboardingService.markTourComplete(currentTourId);
      setCompletedTours(prev => ({ ...prev, [currentTourId]: true }));
    }
    setIsTourActive(false);
    setCurrentTourId(null);
    setCurrentSteps([]);
    setCurrentStepIndex(0);
  }, [currentTourId]);

  const skipTour = useCallback(() => {
    // Don't mark as complete when skipped - user can replay
    setIsTourActive(false);
    setCurrentTourId(null);
    setCurrentSteps([]);
    setCurrentStepIndex(0);
  }, []);

  const nextStep = useCallback(() => {
    if (currentStepIndex < currentSteps.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
    } else {
      // Reached end of tour
      endTour();
    }
  }, [currentStepIndex, currentSteps.length, endTour]);

  const prevStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  }, [currentStepIndex]);

  const goToStep = useCallback((index: number) => {
    if (index >= 0 && index < currentSteps.length) {
      setCurrentStepIndex(index);
    }
  }, [currentSteps.length]);

  const markTourComplete = useCallback((tourId: string) => {
    onboardingService.markTourComplete(tourId);
    setCompletedTours(prev => ({ ...prev, [tourId]: true }));
  }, []);

  const isTourCompleted = useCallback((tourId: string) => {
    return completedTours[tourId] === true;
  }, [completedTours]);

  const resetTour = useCallback((tourId: string) => {
    onboardingService.resetTour(tourId);
    setCompletedTours(prev => {
      const updated = { ...prev };
      delete updated[tourId];
      return updated;
    });
  }, []);

  const currentStep = currentSteps[currentStepIndex] || null;
  const totalSteps = currentSteps.length;

  const value: OnboardingContextType = {
    isTourActive,
    currentTourId,
    currentStepIndex,
    completedTours,
    totalSteps,
    startTour,
    endTour,
    nextStep,
    prevStep,
    skipTour,
    goToStep,
    markTourComplete,
    isTourCompleted,
    resetTour,
    currentStep,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
}

export default OnboardingContext;
